import validEmail from '@repo/utils/helpers/valid-email';
import {
  ADDRESS_CITY,
  ADDRESS_COUNTRY,
  ADDRESS_POSTAL_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
  BIRTHDAY,
  CADENCE,
  CLAY_ERROR,
  CLAY_WARNINGS,
  clayAddedFields,
  COMPANY,
  CSVField,
  CSVRecord,
  CSVRecordWithErrors,
  EMAIL,
  FACEBOOK_URL,
  FIRST_NAME,
  GROUPS,
  HEADLINE,
  LAST_NAME,
  LINKEDIN_URL,
  NAME,
  NOTE,
  PHONE,
  TITLE,
  TWITTER_URL,
  ValidationResult,
  WEBSITE,
} from './vars';
import validURL from '@repo/utils/helpers/valid-url';
import * as chrono from 'chrono-node';
import { CadenceType } from '@repo/utils/models/Cadence';
import { ImportablePerson } from '@repo/redux/actions/csvImport';

type ValidatorFn = (value: string | string[]) => ValidationResult;

const validPhoneNumber = (phoneNumber: string): boolean => /^[\d\s\-\(\)+x]+$/.test(phoneNumber);
const hasValueValidator = (value: string): ValidationResult => ({ valid: !!value });

const emailValidator = (emails: string[]): ValidationResult => {
  if (emails.length === 0) {
    return { valid: false };
  }

  const validEmails = emails.filter(validEmail);
  let error = validEmails.length !== emails.length ? 'Some emails are invalid' : undefined;

  if (error && emails[0].trim().includes(' ')) {
    error = `${error}. Please use a comma to separate multiple emails.`;
  }

  return {
    valid: validEmails.length > 0,
    error,
    sanitizedValue: validEmails,
  };
};

const phoneValidator = (phoneNumbers: string[]): ValidationResult => {
  if (phoneNumbers.length === 0) {
    return { valid: false };
  }

  const validPhoneNumbers = phoneNumbers.filter(validPhoneNumber);
  const invalidPhoneNumberCount = phoneNumbers.length - validPhoneNumbers.length;
  const errorMsg = `${invalidPhoneNumberCount} invalid phone number${
    invalidPhoneNumberCount > 1 ? 's' : ''
  }`;

  return {
    valid: validPhoneNumbers.length > 0,
    error: invalidPhoneNumberCount > 0 ? errorMsg : undefined,
    sanitizedValue: validPhoneNumbers,
  };
};

const urlValidator =
  (domains?: string[]) =>
  (url: string): ValidationResult => {
    if (!url) {
      return { valid: false };
    }

    if (!domains) {
      const valid = validURL(url);
      return { valid, error: valid ? undefined : 'Invalid URL' };
    }

    const valid = validURL(url) && domains.some((domain) => url.includes(domain));
    return {
      valid,
      error: valid ? undefined : `Invalid URL. Must include ${domains.join(' or ')}`,
    };
  };

const groupsValidator = (groups: string[]): ValidationResult => {
  if (groups.length === 0) {
    return { valid: false };
  }

  return { valid: true };
};

// Support yyyy-mm-dd, mm-dd
const dateValidator = (date: string): ValidationResult => {
  if (!date) {
    return { valid: false };
  }

  if (date.includes('-')) {
    // Use Keith's existing logic (roughly) for date parsing for google contact imports
    const transformedDate = date.replaceAll('--', '0-');
    const parts = transformedDate.split('-');
    let year = undefined;
    let month = Number(parts[0]);
    let day = Number(parts[1]);

    if (parts.length === 3) {
      year = Number(parts[0]);
      month = Number(parts[1]);
      day = Number(parts[2]);
    }

    if (isNaN(month) || isNaN(day)) {
      return { valid: false, error: 'Invalid date' };
    }

    return {
      valid: true,
      sanitizedValue: {
        year,
        month,
        day,
      },
    };
  }

  const parts = chrono.parse(date);

  if (!parts || parts.length === 0 || !parts[0]?.start) {
    return { valid: false, error: 'Invalid date' };
  }

  const parsedDate = parts[0].start;

  let year = undefined;
  if (parsedDate.isCertain('year')) {
    year = parsedDate.get('year');
  }

  if (!parsedDate.isCertain('month') || !parsedDate.isCertain('day')) {
    return { valid: false, error: 'Invalid date' };
  }

  return {
    valid: true,
    sanitizedValue: {
      year,
      month: parsedDate.get('month'),
      day: parsedDate.get('day'),
    },
  };
};

const toCadence = (cadence: string): CadenceType => {
  switch (cadence) {
    case 'weekly':
    case 'every week':
    case '7 days':
    case 'every 2 weeks':
    case '14 days':
      return 'weekly';

    case 'monthly':
    case 'every month':
    case '1 mon':
      return 'monthly';

    case 'quarterly':
    case 'every quarter':
    case 'every 3 months':
    case '3 mons':
      return 'quarterly';

    case 'yearly':
    case 'every 6 months':
    case '5 mons':
    case 'every year':
    case '1 year':
      return 'yearly';

    case 'auto':
      return 'auto';

    default:
      return undefined;
  }
};

const cadenceValidator = (cadence: string): ValidationResult => {
  const parsedCadence = toCadence(String(cadence).toLowerCase());

  if (!parsedCadence) {
    return {
      valid: false,
      error: !!cadence
        ? `"${cadence}" is not a valid Cadence. Accepted values: "daily" | "weekly" | "monthly" | "quarterly" | "yearly"`
        : undefined,
    };
  } else {
    return { valid: true, sanitizedValue: parsedCadence };
  }
};

const arrayifyValidation = (validator: ValidatorFn): ValidatorFn => {
  return (value: string | string[]): ValidationResult => {
    const validation = validator(value);

    if (validation.valid) {
      return {
        ...validation,
        sanitizedValue: Array.isArray(value) ? value : [value],
      };
    }

    return validation;
  };
};

const validators: Record<CSVField, ValidatorFn> = {
  [NAME]: hasValueValidator,
  [FIRST_NAME]: hasValueValidator,
  [LAST_NAME]: hasValueValidator,
  [EMAIL]: emailValidator,
  [PHONE]: phoneValidator,
  [LINKEDIN_URL]: arrayifyValidation(urlValidator(['linkedin.com/in/'])),
  [FACEBOOK_URL]: arrayifyValidation(urlValidator(['facebook.com/'])),
  [TWITTER_URL]: arrayifyValidation(urlValidator(['twitter.com/', 'x.com/'])),
  [WEBSITE]: arrayifyValidation(urlValidator()),
  [GROUPS]: groupsValidator,
  [NOTE]: hasValueValidator,
  [TITLE]: hasValueValidator,
  [COMPANY]: hasValueValidator,
  [HEADLINE]: hasValueValidator,
  [ADDRESS_STREET]: hasValueValidator,
  [ADDRESS_CITY]: hasValueValidator,
  [ADDRESS_STATE]: hasValueValidator,
  [ADDRESS_POSTAL_CODE]: hasValueValidator,
  [ADDRESS_COUNTRY]: hasValueValidator,
  [BIRTHDAY]: dateValidator,
  [CADENCE]: cadenceValidator,
};

const headerToKey: Record<CSVField, string> = {
  [NAME]: 'name',
  [FIRST_NAME]: 'first_name',
  [LAST_NAME]: 'last_name',
  [EMAIL]: 'email_addresses',
  [PHONE]: 'phone_numbers',
  [LINKEDIN_URL]: 'linkedin_urls',
  [FACEBOOK_URL]: 'facebook_urls',
  [TWITTER_URL]: 'twitter_urls',
  [NOTE]: 'notes',
  [GROUPS]: 'groups',
  [BIRTHDAY]: 'birthday',
  [TITLE]: 'work_history.title',
  [COMPANY]: 'work_history.company',
  [WEBSITE]: 'websites',
  [HEADLINE]: 'headline',
  [CADENCE]: 'cadence',
  [ADDRESS_STREET]: 'addresses.street',
  [ADDRESS_CITY]: 'addresses.city',
  [ADDRESS_STATE]: 'addresses.state',
  [ADDRESS_POSTAL_CODE]: 'addresses.postal_code',
  [ADDRESS_COUNTRY]: 'addresses.country',
};

export const includesLookup = (mixedCharCols: string[]): boolean => {
  const cols = mixedCharCols.map((col) => col.toLowerCase());

  return (
    cols.includes(NAME) ||
    cols.includes(EMAIL) ||
    cols.includes(PHONE) ||
    cols.includes(LINKEDIN_URL) ||
    cols.includes(FACEBOOK_URL) ||
    cols.includes(TWITTER_URL) ||
    (cols.includes(FIRST_NAME) && cols.includes(LAST_NAME))
  );
};

export function validateRow(record: CSVRecord): {
  importable: boolean;
  sanitizedRecord: ImportablePerson;
  recordWithErrors: CSVRecordWithErrors;
  groupNames: string[];
} {
  const sanitizedRecord = {} as ImportablePerson;
  const recordWithErrors: CSVRecordWithErrors = { ...record };
  let groupNames: string[] = [];

  const [validFields, columnErrors, unsupportedKVTuples] = Object.entries(record).reduce(
    ([validFields, columnErrors, unsupportedKVTuples], [userField, value]) => {
      const field = userField.toLowerCase().trim();

      if (!value || value?.length === 0) {
        return [validFields, columnErrors, unsupportedKVTuples];
      }

      if (!validators[field as CSVField]) {
        if (!!value && !clayAddedFields.includes(field)) {
          return [validFields, columnErrors, [...unsupportedKVTuples, [userField, value]]];
        }
        return [validFields, columnErrors, unsupportedKVTuples];
      }

      const result = validators[field as CSVField](value);

      if (!result.valid && result.error) {
        return [
          validFields,
          [...columnErrors, { field: userField, error: result.error }],
          unsupportedKVTuples,
        ];
      } else {
        const personKey = headerToKey[field as CSVField];

        if (personKey === 'groups') {
          groupNames = result.sanitizedValue || value;
        }

        if (personKey.includes('.')) {
          const [parentKey, propertyKey] = personKey.split('.');
          const existingValue = sanitizedRecord[parentKey]?.[0] || {};
          sanitizedRecord[parentKey] = [
            { ...existingValue, [propertyKey]: result.sanitizedValue || value },
          ];
        } else {
          sanitizedRecord[personKey] = result.sanitizedValue || value;
        }

        return [[...validFields, field], columnErrors, unsupportedKVTuples];
      }
    },
    [[], [], []],
  );

  // use includesLookup on the validFields to determine if the row is importable
  const importable = includesLookup(validFields);

  // if importable, append note with unsupported fields
  if (!importable) {
    recordWithErrors[CLAY_ERROR] =
      'Must include at least one of the following: Name, Email, Phone, LinkedIn URL, Facebook URL, Twitter URL, or First Name + Last Name';
  }

  if (columnErrors.length > 0) {
    recordWithErrors[CLAY_WARNINGS] = columnErrors
      .map(({ field, error }) => `${field}: ${error}`)
      .join(', ');
  }

  if (importable && unsupportedKVTuples.length > 0) {
    const rowNotes = sanitizedRecord[NOTE] || [];
    const unsupportedFields = unsupportedKVTuples
      .map(([field, value]) => `${field}: ${value}`)
      .join('\n');
    sanitizedRecord[NOTE] = [...rowNotes, unsupportedFields];
  }

  return {
    importable,
    sanitizedRecord,
    recordWithErrors,
    groupNames,
  };
}
